/**
 * Login
 */
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { EmailOutlined, WarningOutlined } from "@material-ui/icons";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "../css/Login.css";
import { Axios } from "../utils/UtilityFunctions";
import clsx from "clsx";

import logo from "../images/flyins-logo.svg";
import warning from "../images/warning.svg";

import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  HidePasswordIcon,
  PasswordActiveIcon,
  PasswordIcon,
  ShowPasswordIcon,
} from "../components/CustomIcons";
import { validateEmail } from "../utils/UtilityFunctions";
import { GoogleLogin } from "@react-oauth/google";
import { getApiBaseUrl, getGoogleOAuthClientId } from "../config/env";

const useStyles = makeStyles((theme) => ({
  insidePaper: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  main: {
    marginTop: theme.spacing(7),
    fontFamily: "Inter",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(2, 0, 2),
    backgroundColor: "#5046B2",
    height: 50,
  },
  paper: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(1),
      padding: theme.spacing(4),
    },
  },
  input: {
    "& input:-webkit-autofill": {
      "-webkit-box-shadow": "0 0 0 100px #fafafa inset",
      "-webkit-text-fill-color": "inherit",
    },
  },
  icon: {
    fontSize: 20,
  },
  grid: {
    textAlign: "left",
  },
  googleDivider: {
    width: "100%",
    height: 1,
    marginTop: 24,
    marginBottom: 10,
    backgroundColor: "rgba(0, 0, 0, 0.12)",
  },
  googleSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: 0,
    "& > div": {
      width: "100%",
    },
  },
  textField: {
    "& fieldset": {
      borderColor: "black",
    },
    "&:hover fieldset": {
      borderColor: "black",
    },
    "& input:-webkit-autofill": {
      "-webkit-box-shadow": "0 0 0 100px #fafafa inset",
      "-webkit-text-fill-color": "inherit",
    },
    "& .MuiIconButton-root.MuiIconButton-edgeEnd:hover": {
      background: "transparent",
    },
  },
  errorBox: {
    display: "flex",
    alignItems: "flex-start",
    padding: "12px 16px",
    marginBottom: "16px",
    border: "1px solid #f5222d",
    borderLeft: "4px solid #f5222d",
    borderRadius: "4px",
    backgroundColor: "#fff1f0",
  },
  errorIcon: {
    color: "#f5222d",
    marginRight: "12px",
    marginTop: "2px",
    fontSize: "20px",
    flexShrink: 0,
  },
  errorText: {
    color: "#d9534f",
    fontSize: "14px",
    fontWeight: 500,
  },
  inputErrorMessage: {
    display: "flex",
    alignItems: "center",
    marginLeft: -14,
  },
  warningIcon: {
    height: 14,
    width: 14,
    marginRight: 8,
    marginBottom: 2,
  },
}));

export default function Login() {
  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorPassword, setErrorPassword] = useState("");
  const [toastrMsg, setToastrMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const history = useHistory();
  const googleClientId = getGoogleOAuthClientId();
  const googleSsoInFlight = useRef(false);

  const { t } = useTranslation();

  const handleKeyDown = (event) => {
    if (event.key == "Enter") {
      event.preventDefault();
      submitLogin();
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const submitLogin = async () => {
    setErrorMessage("");
    setToastrMsg("");
    var isError = false;

    if (!email.trim()) {
      isError = true;
      setErrorMessage("Email is required");
    } else if (validateEmail(email) == false) {
      isError = true;
      setErrorMessage("Please enter a valid email");
    }
    
    if (!password.trim()) {
      isError = true;
      setErrorPassword("Password is required");
    }

    if (isError) {
      return;
    }

    let loginUser = await Axios("POST", "/auth/login", {
      email,
      password,
    });

    if (loginUser && loginUser.data.success) {
      localStorage.setItem("userId", loginUser.data.user.token);
      localStorage.setItem("email", loginUser.data.user.email);
      // Store login time to prevent immediate redirect on first API call
      sessionStorage.setItem("lastLoginTime", Date.now().toString());
      window.dispatchEvent(new Event("auth-change"));

      // redirect to dashboard
      history.push("/");
    } else {
      console.log(loginUser);
      setToastrMsg((loginUser && loginUser.data.error) || "Internal error");
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (googleSsoInFlight.current) return;
    setErrorMessage("");
    setToastrMsg("");
    const credential = credentialResponse?.credential;
    if (!credential) {
      setToastrMsg("Google sign-in did not return a token. Please try again.");
      return;
    }
    const baseUrl = getApiBaseUrl();
    if (!baseUrl) {
      setToastrMsg(
        "Missing API base URL. Set REACT_APP_API_BASE_URL_STG or _PROD."
      );
      return;
    }
    googleSsoInFlight.current = true;
    try {
      const res = await axios.post(
        `${baseUrl}/user/sso-login`,
        {},
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${credential}`,
          },
        }
      );
      const data = res.data;
      if (data?.success && data?.user?.token) {
        localStorage.setItem("userId", data.user.token);
        if (data.user.email != null) {
          localStorage.setItem("email", data.user.email);
        }
        sessionStorage.setItem("lastLoginTime", Date.now().toString());
        window.dispatchEvent(new Event("auth-change"));
        history.push("/");
      } else {
        setToastrMsg(data?.message || data?.error || "SSO login failed.");
      }
    } catch (e) {
      const errBody = e.response?.data;
      const msg =
        (typeof errBody === "object" && errBody?.message) ||
        (typeof errBody === "object" && errBody?.error) ||
        (typeof errBody === "string" && errBody) ||
        e.message ||
        "SSO login failed.";
      setToastrMsg(msg);
    } finally {
      googleSsoInFlight.current = false;
    }
  };

  const handleGoogleError = () => {
    setToastrMsg("Google sign-in failed. Please try again.");
  };

  return (
    <>
      <Container
        component="main"
        maxWidth="xs"
        className={clsx(classes.main, "form-style")}
      >
        <CssBaseline />
        <Box
          maxWidth="xs"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <img
            src={logo}
            alt="flyins"
            width="307"
            height="100"
            draggable={false}
          />
          {/* <Grid item xs={12}>
            <Typography
              color="primary"
              style={{
                fontSize: "4rem",
                paddingLeft: "25px",
                fontFamily: "Carter One, cursive",
              }}
            >
              Flyins
            </Typography>
          </Grid> */}
        </Box>

        <div className={classes.paper}>
          <Paper elevation={2}>
            <div className={classes.insidePaper}>
              <Typography
                component="h1"
                variant="h5"
                style={{ marginBottom: 10, fontWeight: 500, fontSize: 40 }}
              >
                {t("Login")}
              </Typography>
              <form className={classes.form} noValidate>
                {googleClientId ? (
                  <>
                    <div className={classes.googleSection}>
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        useOneTap={false}
                        theme="outline"
                        size="large"
                        text="signin_with"
                        shape="rectangular"
                      />
                    </div>
                    <div className={classes.googleDivider} aria-hidden />
                  </>
                ) : null}
                {toastrMsg && (
                  <Box className={classes.errorBox}>
                    <img src={warning} className={classes.errorIcon} />
                    <Typography className={classes.errorText}>
                      {toastrMsg}
                    </Typography>
                  </Box>
                )}
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  className={email !== "" ? classes.textField : classes.input}
                  // className={classes.input}
                  id="email"
                  label={t("Email Address")}
                  error={errorMessage.toLowerCase().includes("email")}
                  helperText={errorMessage.toLowerCase().includes("email") && (
                    <div className={classes.inputErrorMessage}>
                      <img src={warning} className={classes.warningIcon} />
                      <span>{errorMessage}</span>
                    </div>
                  )}
                  name="email"
                  autoComplete="email"
                  value={email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined
                          className={classes.icon}
                          style={{
                            color: email.length ? "#000000" : "#868e96",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  className={email !== "" ? classes.textField : classes.input}
                  name="password"
                  label={t("Password")}
                  type={showPassword ? "text" : "password"}
                  id="password"
                  error={errorPassword.toLowerCase().includes("password")}
                  helperText={errorPassword.toLowerCase().includes("password") && (
                    <div className={classes.inputErrorMessage}>
                      <img src={warning} className={classes.warningIcon} />
                      <span>{errorPassword}</span>
                    </div>
                  )}
                  value={password}
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {password === "" ? (
                          <PasswordIcon
                            className={classes.icon}
                            style={{ marginTop: 10 }}
                          />
                        ) : (
                          <PasswordActiveIcon
                            className={classes.icon}
                            style={{ marginTop: 10 }}
                          />
                        )}
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePassword} edge="end">
                          {showPassword ? (
                            <ShowPasswordIcon
                              className={classes.icon}
                              style={{ marginTop: 10 }}
                            />
                          ) : (
                            <HidePasswordIcon
                              className={classes.icon}
                              style={{ marginTop: 10 }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Grid container className={classes.grid}>
                  <Grid item xs>
                    <Link href="forgotpassword" variant="body2">
                      {t("Forgot password?")}
                    </Link>
                  </Grid>
                </Grid>
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  color="primary"
                  id="submit"
                  className={classes.submit}
                  onClick={submitLogin}
                >
                  {t("Log In")}
                </Button>
                <Grid container style={{ textAlign: "center" }}>
                  <Grid item xs>
                    {t("Don't have an account? ")}
                    <Link href="createaccount" variant="body2">
                      {t("Create account")}
                    </Link>
                  </Grid>
                </Grid>
              </form>
            </div>
          </Paper>
        </div>
      </Container>
    </>
  );
}
